import { useDispatch } from 'react-redux';
import Location from '@solvhealth/types/interfaces/Location';
import { useQueryClient } from 'react-query';
import { useContext, useState } from 'react';
import { setPastBooking } from '~/actions/pastBookings';
import {
  LOCATION_PAPERWORK_RESPONSE_TYPE_REGISTRATION,
  PaperworkSectionRoute,
  ProviderGroupPaperworkSectionRoute,
  PaperworkStatus,
  PaperworkFormName,
  ProviderGroupPaperworkFormName,
} from '~/components/Paperwork/paperworkConstants';
import { apiGetJson } from '~/core/dapi';
import { getPublicBookingByIdUrl } from '~/core/dapi/waitList';
import { getDevice } from '~/core/util/device';
import { emptyFunction } from '~/core/util/function';
import {
  isMobileCheckInEnabled,
  isProviderGroupLocation,
  isTelemedLocation,
} from '~/core/util/location';
import { getNextRouteForMobileCheckInFlowAfterPaperwork } from '~/core/util/mobileCheckIn';
import { getNextRouteForTelemedFlowAfterPaperwork } from '~/core/util/videoVisit';
import { incrementProgressBar } from '~/ducks/paperwork';
import { useSolvSelector } from '~/reducers';
import { PaperworkSagas } from '~/sagas/paperwork';
import { analyticsTrackEvent } from '../../../../../core/analytics';
import {
  PAPERWORK_AUTOFILL_STATS,
  PAPERWORK_JUMP_TO_SECTION,
} from '../../../../../core/analytics/events';
import history from '../../../../../core/history';
import { getProfileFromBooking } from '~/core/util/account';
import { setMetaPaperwork } from '~/ducks/paperworkMeta';
import { validateFamilyBooking } from '../../../../../util/validateFamilyBooking';
import { IS_COMING_FROM_HANDOFF_COOKIE_NAME } from '../../../../../config';
import { doesCookieExist } from '../../../../../core/util/cookies';
import { useAutofillPaperworkStats } from '~/components/Paperwork/hooks/useAutofillpaperworkStats';
import AuthenticationContext from '~/core/login/AuthenticationContext';

const usePushToPaperworkComplete = (bookingId: string) => {
  const dispatch = useDispatch();
  const photoId = useSolvSelector((state) => state.photoId);
  const location = useSolvSelector((state) => state.location) as Location;
  const booking = useSolvSelector((state) => state.pastBookings[bookingId]);
  const paperwork = useSolvSelector((state) => state.paperwork);
  const accountSummary = useSolvSelector((state) => state.account.summary);
  const isProviderGroup = isProviderGroupLocation(location);
  const { isPrimary, isFamilyBooking } = validateFamilyBooking(booking);
  const client = useQueryClient();
  const { isLoggedIn } = useContext(AuthenticationContext);

  const paperworkAutofillStats = useAutofillPaperworkStats(bookingId);

  const [isComingFromHandoff, setIsComingFromHandoff] = useState(
    doesCookieExist(IS_COMING_FROM_HANDOFF_COOKIE_NAME)
  );

  const pushToPaperworkComplete = () => {
    const from = isProviderGroup
      ? ProviderGroupPaperworkSectionRoute.FINALIZE
      : PaperworkSectionRoute.FINALIZE;
    analyticsTrackEvent(PAPERWORK_JUMP_TO_SECTION, {
      from,
      to: `${PaperworkSectionRoute.COMPLETE}/${bookingId}`,
      bookingId,
      isProviderGroupLocation: isProviderGroup,
      isLoggedIn,
    });
    analyticsTrackEvent(PAPERWORK_AUTOFILL_STATS, { ...paperworkAutofillStats, isLoggedIn });

    dispatch({
      type: PaperworkSagas.SUBMIT_FINAL_PAPERWORK_PAGE,
      formData: {
        response_fields: { paperworkStatus: PaperworkStatus.COMPLETE },
        photo_id_ids: { front: photoId?.front?.id, back: photoId?.back?.id },
        booking_id: bookingId,
        type_: LOCATION_PAPERWORK_RESPONSE_TYPE_REGISTRATION,
        is_editing: false,
      },
      // re-fetch booking for accurate checklist if redirected back to /pass screen
      onSuccess: () => {
        apiGetJson(getPublicBookingByIdUrl(bookingId)).then((response) => {
          dispatch(setPastBooking(response.data));
        });
        client.invalidateQueries('query_child_bookings');
      },
      onError: emptyFunction,
    });

    const lastFormCompleted = isProviderGroup
      ? ProviderGroupPaperworkFormName.REVIEW
      : PaperworkFormName.REVIEW;

    dispatch({
      type: PaperworkSagas.SUBMIT_PAPERWORK_STATUS_LOG,
      logData: {
        booking_id: bookingId,
        paperwork_status: PaperworkStatus.COMPLETE,
        device_type: getDevice(),
        last_form_completed: lastFormCompleted,
      },
    });

    if (!isProviderGroup && isFamilyBooking && isPrimary) {
      dispatch(setMetaPaperwork(paperwork.formData));
    }

    if (!isProviderGroup && !isFamilyBooking && !isComingFromHandoff) {
      const userProfile = getProfileFromBooking(accountSummary, booking) || {};
      if (isTelemedLocation(location)) {
        history.push(getNextRouteForTelemedFlowAfterPaperwork(booking, location, userProfile));
        return;
      }

      if (isMobileCheckInEnabled(location) && !paperwork.isComingFromPass) {
        history.push(
          getNextRouteForMobileCheckInFlowAfterPaperwork(booking, location, userProfile)
        );
        return;
      }
    }

    history.push(`${PaperworkSectionRoute.COMPLETE}/${bookingId}`);
    dispatch(incrementProgressBar(100));
  };

  return pushToPaperworkComplete;
};

export default usePushToPaperworkComplete;
